import { useMutation } from '@apollo/client';
import {
  DeleteImpactRatingsDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useDeleteImpactRatingsTRPC } from './useDeleteImpactRatingsTRPC';

export const useDeleteImpactRatings = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [deleteImpactRatingsGraphQL, graphqlState] = useMutation(
    DeleteImpactRatingsDocument,
    {
      update: (cache) => evictField(cache, 'impact_rating'),
      // The impactRating nav badge count query uses fetchPolicy: 'no-cache',
      // so evicting cache fields does not refresh it — refetch it explicitly.
      refetchQueries: [namedOperations.Query.getImpactRatingCount],
    }
  );

  // tRPC mutation
  const trpcMutation = useDeleteImpactRatingsTRPC();

  const deleteImpactRatings = async (variables: {
    Ids: string[];
  }): Promise<void> => {
    if (trpcEnabled) {
      return trpcMutation.deleteImpactRatings(variables);
    }

    const result = await deleteImpactRatingsGraphQL({
      variables: {
        Ids: variables.Ids,
      },
    });

    if (!result.data) {
      throw new Error('Failed to delete impact ratings');
    }
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      deleteImpactRatings,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    deleteImpactRatings,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
